import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The scope a `CanvasLayer` subtree draws in: its own canvas. It takes the node,
 * not derived values, so the Node2D walk (`NodeDispatcher`) and the Control walk's
 * `CanvasLayer` painter publish the same four facts and the same canvas transform.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { threeMatrixFromTransform2D } from './node2dTransform.js';
import { canvasLayerOf } from './canvasPaintOrder.js';
import { useCanvasModulateColor, CanvasModulateContext } from './canvasModulate.js';
import { Modulate2DContext, WHITE_MODULATE } from './canvasItemModulate.js';
import { ROOT_TEXTURE_SAMPLER, TextureSampler2DContext } from './canvasItemTextureSampler.js';
import { CanvasLayerIndexProvider, EffectiveZProvider } from './lighting2d/canvasItemPlacement.js';
import { isViewportPass, observeSceneCamera } from './sceneRenderCamera.js';
import { viewportLayerMatrix } from './viewportLayerAnchor.js';
import { CanvasFrameContext } from './canvasFrame.js';
/** Places `layer` for each render of the scene, unanchored outside a sub-viewport's pass. */
function useViewportLayerAnchor(layer, follow) {
    const scene = useThree((state) => state.scene);
    const { enabled, scale } = follow;
    useEffect(() => {
        const size = new THREE.Vector2();
        const next = new THREE.Matrix4();
        return observeSceneCamera(scene, (camera, target) => {
            const object = layer.current;
            if (!object)
                return;
            if (!target || !isViewportPass(camera))
                next.identity();
            else
                viewportLayerMatrix({ enabled, scale }, camera, size.set(target.width, target.height), next);
            if (next.equals(object.matrix))
                return;
            object.matrix.copy(next);
            // `onBeforeRender` runs after the scene's own matrix update, so the subtree is refreshed here.
            object.updateMatrixWorld(true);
        });
    }, [scene, layer, enabled, scale]);
}
/**
 * Under a `CanvasLayer`, a `Node`, `get_parent_item()` returns null
 * (`scene/main/canvas_item.cpp:565`): the child attaches to the layer's canvas RID
 * (`canvas_item.cpp:264,269`), so inherited modulate restarts white
 * (`servers/rendering/renderer_canvas_cull.cpp:82`) and the sampler at the root default.
 */
export function CanvasLayerScope({ node, children }) {
    // A CanvasModulate is per canvas: the world's stays out, and one inside tints
    // only this layer. The texture caches test the same null `parent_item`
    // (`canvas_item.cpp:1625-1699`). Visibility crosses only because
    // `scene/main/canvas_layer.cpp:57-63` propagates it by hand.
    const modulate = useCanvasModulateColor(node.children);
    // Godot draws a layer's canvas through the layer's transform (`renderer_viewport.cpp:70-74`),
    // under the anchor a sub-viewport pass through a Camera2D sets.
    const { canvasTransform, follow_viewport_enabled, follow_viewport_scale } = node.properties;
    const matrix = useMemo(() => threeMatrixFromTransform2D(canvasTransform), [canvasTransform]);
    const anchor = useRef(null);
    // State, not a ref: a light reads the frame once it mounts.
    const [frame, setFrame] = useState(null);
    useViewportLayerAnchor(anchor, { enabled: follow_viewport_enabled, scale: follow_viewport_scale });
    // The layer index picks the 2D lights: a light reaches a canvas whose layer is
    // in its `range_layer_min/max`, default 0..0, where a layer defaults to 1. z
    // restarts at 0, since `_cull_canvas_item` walks each canvas from 0.
    return (_jsx(CanvasModulateContext.Provider, { value: modulate, children: _jsx(Modulate2DContext.Provider, { value: WHITE_MODULATE, children: _jsx(TextureSampler2DContext.Provider, { value: ROOT_TEXTURE_SAMPLER, children: _jsx(CanvasLayerIndexProvider, { value: canvasLayerOf(node), children: _jsx(EffectiveZProvider, { value: 0, children: _jsx("object3D", { ref: anchor, matrixAutoUpdate: false, children: _jsx("object3D", { ref: setFrame, matrix: matrix, matrixAutoUpdate: false, children: _jsx(CanvasFrameContext.Provider, { value: frame, children: children }) }) }) }) }) }) }) }));
}
//# sourceMappingURL=canvasLayerScope.js.map